import React, { useState, useEffect, useRef } from "react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { SERVER_URL } from "../config";
import "../styles/prescriptions/providerNotesCard.css";

export interface ProviderNote {
  id: string;
  note: string;
  createdAt: string; // ISO date string
}

interface ProviderNotesCardProps {
  initialNotes?: ProviderNote[];
  patientProfileId: string;
  onSave: (newNote: string) => void;
}

const ProviderNotesCard: React.FC<ProviderNotesCardProps> = ({
  initialNotes = [],
  patientProfileId,
  onSave,
}) => {
  const [notes, setNotes] = useState<ProviderNote[]>(initialNotes);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch notes from patientProfile on mount.
  useEffect(() => {
    const fetchNotes = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetchWithAuth(
          `${SERVER_URL}/api/providerNote/provider-notes?patientProfileId=${patientProfileId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.ok) {
          const data: ProviderNote[] = await response.json();
          setNotes(data);
        } else {
          console.error("Failed to fetch provider notes.");
        }
      } catch (error) {
        console.error("Error fetching provider notes:", error);
      }
    };

    fetchNotes();
  }, [patientProfileId]);

  const handleSave = async () => {
    if (newNote.trim() === "") return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetchWithAuth(`${SERVER_URL}/api/providerNote/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          patientProfileId,
          note: newNote,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        alert("Error: " + errorData.error);
      } else {
        const createdNote: ProviderNote = await response.json();
        setNotes([...notes, createdNote]);
        onSave(newNote);
        setNewNote("");
      }
    } catch (error) {
      console.error("Error creating provider note:", error);
      alert("Failed to create provider note.");
    } finally {
      setLoading(false);
    }
  };

  // When expanded, listen to scrolling and load 5 more notes when near bottom.
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!expanded) return;
    const target = e.target as HTMLDivElement;
    if (
      target.scrollHeight - target.scrollTop - target.clientHeight < 50 &&
      visibleCount < notes.length
    ) {
      setVisibleCount((prev) => Math.min(prev + 5, notes.length));
    }
  };

  // Updated: Increase visibleCount to 10 when expanding
  const handleExpand = () => {
    setExpanded(true);
    setVisibleCount(Math.min(notes.length, 10));
  };

  const renderedNotes = expanded
    ? notes.slice(0, visibleCount)
    : notes.slice(0, 5);

  return (
    <div
      className="provider-notes-card"
      ref={containerRef}
      onScroll={handleScroll}
    >
      <h3>Provider Notes</h3>
      <div>
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Write your note here..."
          style={{
            width: "100%",
            height: "120px",
            padding: "8px",
            resize: "vertical",
          }}
        />
        <p>
          notes are visible by other providers and attatched to the patient's
          profile, these are only visible to providers and never patients
        </p>
        <button onClick={handleSave} className="save-button" disabled={loading}>
          {loading ? "Saving..." : "Save"}
        </button>
        <hr style={{ border: "1px solid black" }} />
      </div>
      {renderedNotes.length > 0 ? (
        renderedNotes.map((note) => (
          <div key={note.id} style={{ marginBottom: "12px" }}>
            <p>{note.note}</p>
            <small>{new Date(note.createdAt).toLocaleString()}</small>
            <hr style={{ border: "1px solid #ccc" }} />
          </div>
        ))
      ) : (
        <p>No provider notes yet.</p>
      )}

      {/* If not expanded and more than 5 notes exist, show an expand button with three dots */}
      {!expanded && notes.length > 5 && (
        <button
          onClick={handleExpand}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: "24px",
            color: "gray",
          }}
        >
          &hellip;
        </button>
      )}
    </div>
  );
};

export default ProviderNotesCard;
